import type { VariantProps } from 'class-variance-authority';
import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Badge,
  badgeVariants,
  Button,
  Card,
  CardContent,
} from '@/components/atoms';
import { DataTable } from '@/components/molecules';
import { ChevronLeftIcon, ChevronsRightIcon } from 'lucide-react';
import { TransactionsEmptyState } from './transactions-empty-state';
import type { TransactionUI } from '../types';

const transactionStatusMap: Record<
  TransactionUI['status'],
  VariantProps<typeof badgeVariants>['variant']
> = {
  success: 'success',
  pending: 'warning',
  failed: 'destructive',
};

interface TransactionsTableProps {
  data: TransactionUI[];
  onView: (transaction: TransactionUI) => void;
}

export const TransactionsTable = ({ data, onView }: TransactionsTableProps) => {
  const columns = useMemo<ColumnDef<TransactionUI>[]>(
    () => [
      {
        accessorKey: 'refId',
        header: 'Ref ID',
      },
      {
        accessorKey: 'orderId',
        header: 'Order ID',
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="font-semibold">{row.original.amount}</span>
        ),
      },
      {
        accessorKey: 'method',
        header: 'Method',
      },
      {
        accessorKey: 'gateway',
        header: 'Gateway',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={transactionStatusMap[row.original.status]}>
            {row.original.statusLabel}
          </Badge>
        ),
      },
      {
        accessorKey: 'date',
        header: 'Date',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onView(row.original)}
          >
            View
          </Button>
        ),
      },
    ],
    [onView]
  );

  if (data.length === 0) {
    return <TransactionsEmptyState />;
  }

  return (
    <Card className="border-none shadow-none mt-4 pt-0">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <DataTable columns={columns} data={data} />
        </div>

        <div className="p-4 pt-6">
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              className="h-10 w-10 rounded-lg bg-primary text-white hover:bg-primary/90"
            >
              1
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 rounded-lg border-none bg-primary/5 text-primary hover:bg-primary/10"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 rounded-lg border-none bg-primary/5 text-primary hover:bg-primary/10"
            >
              <ChevronsRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
